<template>
  <div ref="wrapperRef" class="relative">
    <!-- Button -->
    <button
      type="button"
      class="flex w-full items-center justify-between rounded border border-border-base bg-bg-base px-12 text-left text-text-base hover:border-border-strong transition-colors"
      :class="[inputHeight, { 'cursor-not-allowed opacity-60': loading }]"
      :disabled="loading"
      @click="toggle"
    >
      <span class="inline-flex items-center gap-8">
        <GlobalIcon
          v-if="showLeadingIcon && leadingIcon"
          :name="leadingIcon"
          size="small"
        />
        <span
          :class="{ 'type-base': design === 'lg', 'type-sm': design !== 'lg' }"
        >
          {{ selectedLabel }}
        </span>
      </span>
      <LoadingIcon v-if="loading" class="w-16 h-16 ml-8" />
      <GlobalIcon
        v-else
        name="angle-down"
        size="small"
        class="transition-transform shrink-0"
        :class="{ 'rotate-180': showDropdown }"
      />
    </button>

    <!-- Dropdown Options -->
    <div
      v-if="showDropdown"
      class="absolute z-20 mt-2 max-h-208 w-full overflow-auto rounded border border-border-subtle bg-bg-base py-4 text-text-base shadow-lg"
      :class="{ 'type-base': design === 'lg', 'type-sm': design !== 'lg' }"
    >
      <ul>
        <li
          v-for="option in options"
          :key="option.value"
          class="flex cursor-pointer select-none items-center justify-between px-12 py-8 hover:bg-bg-subtle"
          :class="[
            modelValue?.value === option.value ? 'bg-bg-subtle' : 'bg-bg-base',
            option.borderTop ? 'border-t border-border-subtle' : ''
          ]"
          @click="select(option)"
        >
          <span class="flex items-center gap-8" :class="[modelValue?.value === option.value ? 'font-semibold' : 'font-normal']">
            <GlobalIcon
              v-if="option.icon"
              :name="option.icon"
              size="small"
            />
            {{ option.label }}
          </span>
          <img
            v-if="modelValue?.value === option.value"
            src="/icons/check.svg"
            class="w-16 h-16"
          >
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { onClickOutside } from '@vueuse/core';
import LoadingIcon from '~/components/generic/LoadingIcon.vue';

interface Option {
  value: string | number;
  label: string;
  icon?: string;
  borderTop?: boolean;
}

const props = defineProps<{
  options: Option[];
  modelValue: Option | null;
  design?: 'lg' | 'base' | 'md' | 'sm';
  loading?: boolean;
  showLeadingIcon?: boolean;
  leadingIcon?: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: Option): void;
}>();

const showDropdown = ref(false);
const wrapperRef = ref();

// selected label
const selectedLabel = computed(() => props.modelValue?.label ?? '');

// select
const select = (option: Option) => {
  emit('update:modelValue', option);
  showDropdown.value = false;
};

const toggle = () => {
  showDropdown.value = !showDropdown.value;
};

onClickOutside(wrapperRef, () => {
  showDropdown.value = false;
});

const inputHeight = computed(() => {
  if (props.design === 'sm') return 'h-32';
  if (props.design === 'md') return 'h-36';
  if (props.design === 'base') return 'h-40';
  return 'h-48'; //
});

</script>